<script setup lang="ts">
import { computed, inject, ref, type Ref } from 'vue';
import { type SerializedUser } from '@/api/users';
import { usePermissions } from '@/composables/usePermissions';
import BaseServicePricesList from './base-service-prices-list.vue';
import HolidayMultipliersForm from './holiday-multipliers-form.vue';
import TaskCategoriesList from './task-categories-list.vue';
import PatientServiceCategoriesList from './patient-service-categories-list.vue';
import TaskDefinitionsList from './task-definitions-list.vue';
import OfficesList from './offices-list.vue';
import ServiceCompaniesList from './service-companies-list.vue';
import UsersList from '../users/users-list.vue';
import GroupsTabs from './groups-tabs.vue';
import TaxSettingsForm from './tax-settings-form.vue';

const currentUser = inject<Ref<SerializedUser>>('currentUser');

const { hasPermission } = usePermissions();

const tabs = [
  {
    key: 'service-prices',
    title: 'Precios Base',
    icon: 'mdi-currency-usd',
    component: BaseServicePricesList,
    available: hasPermission.value('service_prices', 'index'),
  },
  {
    key: 'holiday-multipliers',
    title: 'Feriados',
    icon: 'mdi-calendar-star',
    component: HolidayMultipliersForm,
    available: hasPermission.value('service_prices', 'index'),
  },
  {
    key: 'task-categories',
    title: 'Categorías de Tareas',
    icon: 'mdi-tag-multiple',
    component: TaskCategoriesList,
    available: hasPermission.value('tasks', 'index'),
  },
  {
    key: 'task-definitions',
    title: 'Definiciones de Tareas',
    icon: 'mdi-file-document-multiple',
    component: TaskDefinitionsList,
    available: hasPermission.value('tasks', 'index'),
  },
  {
    key: 'patient-service-categories',
    title: 'Categorías de Pacientes',
    icon: 'mdi-tag-heart',
    component: PatientServiceCategoriesList,
    available: currentUser?.value?.companyPatientServiceCategoriesEnabled &&
      hasPermission.value('patient_service_categories', 'index'),
  },
  {
    key: 'offices',
    title: 'Oficinas',
    icon: 'mdi-office-building',
    component: OfficesList,
    available: currentUser?.value?.companyAuditable,
  },
  {
    key: 'service-companies',
    title: 'Empresas de Servicio',
    icon: 'mdi-domain',
    component: ServiceCompaniesList,
    available: currentUser?.value?.companyAuditable,
  },
  {
    key: 'users',
    title: 'Usuarios',
    icon: 'mdi-account-multiple',
    component: UsersList,
    available: hasPermission.value('users', 'index'),
  },
  {
    key: 'groups',
    title: 'Grupos',
    icon: 'mdi-account-group',
    component: GroupsTabs,
    available: currentUser?.value?.companyGroupsEnabled && hasPermission.value('groups', 'manage'),
  },
  {
    key: 'tax-settings',
    title: 'Facturación electrónica',
    icon: 'mdi-file-document-check',
    component: TaxSettingsForm,
    // Kuido enables billing per company; until then the section does not exist.
    available: currentUser?.value?.companyIssuesTaxDocuments &&
      hasPermission.value('tax_settings', 'manage'),
  },
];

const filteredTabs = computed(() => tabs.filter((tab) => tab.available));
const activeTab = ref(filteredTabs.value[0]?.key);
</script>
<template>
  <v-container
    v-if="hasPermission('settings', 'index')"
  >
    <v-row>
      <v-col cols="12">
        <h1 class="mb-6 text-h4">
          Configuración
        </h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        md="3"
      >
        <v-card>
          <v-card-text class="pa-2">
            <v-list
              nav
              density="compact"
            >
              <v-list-item
                v-for="tab in filteredTabs"
                :key="tab.key"
                :active="activeTab === tab.key"
                :value="tab.key"
                class="mb-1"
                @click="activeTab = tab.key"
              >
                <template #prepend>
                  <v-icon
                    :icon="tab.icon"
                    class="me-3"
                  />
                </template>
                <v-list-item-title>{{ tab.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="9"
      >
        <div
          v-for="tab in tabs"
          :key="tab.key"
        >
          <component
            :is="tab.component"
            v-if="activeTab === tab.key"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>
